import React, { useState, useEffect } from "react";
import { ref, push, onValue, set, update, remove, query, orderByChild, limitToLast, get } from "../../Config/matuRtdb";
import { v4 as uuidv4 } from "uuid";
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "../../Config/matuStorage";
import {
  Heart,
  Send,
  Image as ImageIcon,
  MessageCircle,
  Share,
  Bookmark,
  MoreHorizontal,
  X,
  Smile,
  MapPin,
  AlertCircle,
  Camera,
  Plus,
  Crown,
  Verified,
  TrendingUp,
  Flame,
  Star,
  Users,
  Eye,
  Clock,
  Filter,
  Search,
  Hash,
  Award,
  Zap,
} from "lucide-react";
import { auth, database } from "../../Config/firebase";
import useUserData from "../../Hooks/useUserData";
import FootballLoading from "../../Utils/FootballLoading";
import FreePay from "../Banner/HomeFree";

const storage = getStorage();

// Emojis populares organizados por categorías
const emojiCategories = {
  frequent: ["😀", "😂", "❤️", "👍", "👏", "🔥", "💯", "🙌", "😍", "🤔"],
  smileys: [
    "😀",
    "😃",
    "😄",
    "😁",
    "😆",
    "😅",
    "😂",
    "🤣",
    "😊",
    "😇",
    "🙂",
    "🙃",
    "😉",
    "😌",
    "😍",
    "🥰",
    "😘",
    "😗",
    "😙",
    "😚",
    "😋",
    "😛",
    "😝",
    "😜",
    "🤪",
    "🤨",
    "🧐",
    "🤓",
    "😎",
    "🤩",
    "🥳",
  ],
  emotions: [
    "😭",
    "😢",
    "😤",
    "😠",
    "😡",
    "🤬",
    "😱",
    "😨",
    "😰",
    "😥",
    "😓",
    "🤗",
    "🤔",
    "🤭",
    "🤫",
    "🤥",
    "😶",
    "😐",
    "😑",
    "😬",
    "🙄",
    "😯",
    "😦",
    "😧",
    "😮",
    "😲",
  ],
  gestures: [
    "👍",
    "👎",
    "👌",
    "🤌",
    "🤏",
    "✌️",
    "🤞",
    "🤟",
    "🤘",
    "🤙",
    "👈",
    "👉",
    "👆",
    "🖕",
    "👇",
    "☝️",
    "👏",
    "🙌",
    "👐",
    "🤲",
    "🤝",
    "🙏",
  ],
  objects: [
    "⚽",
    "🏀",
    "🏈",
    "⚾",
    "🎾",
    "🏐",
    "🏉",
    "🎱",
    "🏓",
    "🏸",
    "🏒",
    "🏑",
    "🥍",
    "🏏",
    "🥅",
    "⛳",
    "🪃",
    "🥏",
    "🎯",
    "🪀",
    "🎮",
    "🕹️",
    "🎰",
    "🎲",
    "🧩",
    "🃏",
  ],
  symbols: [
    "❤️",
    "🧡",
    "💛",
    "💚",
    "💙",
    "💜",
    "🖤",
    "🤍",
    "🤎",
    "💔",
    "❣️",
    "💕",
    "💞",
    "💓",
    "💗",
    "💖",
    "💘",
    "💝",
    "💟",
    "☮️",
    "✝️",
    "☪️",
    "🕉️",
    "☸️",
    "✡️",
    "🔯",
    "🕎",
    "☯️",
    "☦️",
    "🛐",
    "⛎",
    "♈",
    "♉",
    "♊",
    "♋",
    "♌",
    "♍",
    "♎",
    "♏",
    "♐",
    "♑",
    "♒",
    "♓",
    "🆔",
    "⚛️",
    "🉑",
    "☢️",
    "☣️",
    "📴",
    "📳",
    "🈶",
    "🈚",
    "🈸",
    "🈺",
    "🈷️",
    "✴️",
    "🆚",
    "💮",
    "🉐",
    "㊙️",
    "㊗️",
    "🈴",
    "🈵",
    "🈹",
    "🈲",
    "🅰️",
    "🅱️",
    "🆎",
    "🆑",
    "🅾️",
    "🆘",
    "❌",
    "⭕",
    "🛑",
    "⛔",
    "📛",
    "🚫",
    "💯",
    "💢",
    "♨️",
    "🚷",
    "🚯",
    "🚳",
    "🚱",
    "🔞",
    "📵",
    "🚭",
    "❗",
    "❕",
    "❓",
    "❔",
    "‼️",
    "⁉️",
    "🔅",
    "🔆",
    "〽️",
    "⚠️",
    "🚸",
    "🔱",
    "⚜️",
    "🔰",
    "♻️",
    "✅",
    "🈯",
    "💹",
    "❇️",
    "✳️",
    "❎",
    "🌐",
    "💠",
    "Ⓜ️",
    "🌀",
    "💤",
    "🏧",
    "🚾",
    "♿",
    "🅿️",
    "🈳",
    "🈂️",
    "🛂",
    "🛃",
    "🛄",
    "🛅",
    "🚹",
    "🚺",
    "🚼",
    "🚻",
    "🚮",
    "🎦",
    "📶",
    "🈁",
    "🔣",
    "ℹ️",
    "🔤",
    "🔡",
    "🔠",
    "🆖",
    "🆗",
    "🆙",
    "🆒",
    "🆕",
    "🆓",
    "0️⃣",
    "1️⃣",
    "2️⃣",
    "3️⃣",
    "4️⃣",
    "5️⃣",
    "6️⃣",
    "7️⃣",
    "8️⃣",
    "9️⃣",
    "🔟",
    "🔢",
    "#️⃣",
    "*️⃣",
    "⏏️",
    "▶️",
    "⏸️",
    "⏯️",
    "⏹️",
    "⏺️",
    "⏭️",
    "⏮️",
    "⏩",
    "⏪",
    "⏫",
    "⏬",
    "◀️",
    "🔼",
    "🔽",
    "➡️",
    "⬅️",
    "⬆️",
    "⬇️",
    "↗️",
    "↘️",
    "↙️",
    "↖️",
    "↕️",
    "↔️",
    "↪️",
    "↩️",
    "⤴️",
    "⤵️",
    "🔀",
    "🔁",
    "🔂",
    "🔄",
    "🔃",
    "🎵",
    "🎶",
    "➕",
    "➖",
    "➗",
    "✖️",
    "♾️",
    "💲",
    "💱",
    "™️",
    "©️",
    "®️",
    "〰️",
    "➰",
    "➿",
    "🔚",
    "🔙",
    "🔛",
    "🔝",
    "🔜",
    "✔️",
    "☑️",
    "🔘",
    "🔴",
    "🟠",
    "🟡",
    "🟢",
    "🔵",
    "🟣",
    "⚫",
    "⚪",
    "🟤",
    "🔺",
    "🔻",
    "🔸",
    "🔹",
    "🔶",
    "🔷",
    "🔳",
    "🔲",
    "▪️",
    "▫️",
    "◾",
    "◽",
    "◼️",
    "◻️",
    "🟥",
    "🟧",
    "🟨",
    "🟩",
    "🟦",
    "🟪",
    "⬛",
    "⬜",
    "🟫",
    "🔈",
    "🔇",
    "🔉",
    "🔊",
    "🔔",
    "🔕",
    "📣",
    "📢",
    "👁‍🗨",
    "💬",
    "💭",
    "🗯️",
    "♠️",
    "♣️",
    "♥️",
    "♦️",
    "🃏",
    "🎴",
    "🀄",
    "🕐",
    "🕑",
    "🕒",
    "🕓",
    "🕔",
    "🕕",
    "🕖",
    "🕗",
    "🕘",
    "🕙",
    "🕚",
    "🕛",
    "🕜",
    "🕝",
    "🕞",
    "🕟",
    "🕠",
    "🕡",
    "🕢",
    "🕣",
    "🕤",
    "🕥",
    "🕦",
    "🕧",
  ],
};

export default function SocialWorld() {
  const [posts, setPosts] = useState([]);
  const [newPost, setNewPost] = useState({ text: "", image: null });
  const [selectedImage, setSelectedImage] = useState(null);
  const [comments, setComments] = useState({});
  const [showComments, setShowComments] = useState({});
  const [newComment, setNewComment] = useState({});
  const [savedPosts, setSavedPosts] = useState({});
  const [isPosting, setIsPosting] = useState(false);
  const [error, setError] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [emojiPickerFor, setEmojiPickerFor] = useState(null); // 'post' o postId para comentarios
  const [activeEmojiCategory, setActiveEmojiCategory] = useState("frequent");
  const [showImagePreview, setShowImagePreview] = useState(null);
  
  // Nuevos estados para funcionalidades avanzadas
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all"); // all, vip, trending, recent
  const [showTrending, setShowTrending] = useState(false);
  const [trendingPosts, setTrendingPosts] = useState([]);
  const [userStats, setUserStats] = useState({});
  const [showUserProfile, setShowUserProfile] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMorePosts, setHasMorePosts] = useState(true);
  const [postLimit, setPostLimit] = useState(20);
  
  // Estados para ubicación y hashtags
  const [userLocation, setUserLocation] = useState(null);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [hashtags, setHashtags] = useState([]);
  const [trendingHashtags, setTrendingHashtags] = useState([]);
  const [selectedHashtag, setSelectedHashtag] = useState(null);

  const { userData, loading, error: userError } = useUserData();

  // Función para detectar y bloquear enlaces, emails y contenido no permitido
  const validateContent = (text) => {
    const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+|[^\s]+\.[a-z]{2,})/gi;
    const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/gi;
    const phoneRegex =
      /(\+?[0-9]{1,4}[-.\s]?)?[0-9]{3,4}[-.\s]?[0-9]{3,4}[-.\s]?[0-9]{3,4}/gi;

    if (urlRegex.test(text)) {
      return "No se permiten enlaces o URLs";
    }
    if (emailRegex.test(text)) {
      return "No se permiten direcciones de correo electrónico";
    }
    if (phoneRegex.test(text)) {
      return "No se permiten números de teléfono";
    }
    return null;
  };

  // Función para verificar si un usuario es VIP
  const isUserVip = (userData) => {
    return userData?.typeU === "Cuenta Vip" || 
           userData?.vipStatus === "active" || 
           userData?.plan === "vip" || 
           userData?.isVip === true;
  };

  // Función para obtener el color VIP del usuario
  const getVipColor = (userData) => {
    if (isUserVip(userData)) {
      return "from-yellow-400 via-orange-500 to-red-500";
    }
    return "from-gray-400 to-gray-600";
  };

  // Función para obtener el badge VIP del usuario
  const getVipBadge = (userData) => {
    if (isUserVip(userData)) {
      return (
        <div className="flex items-center gap-1">
          <Crown className="w-4 h-4 text-yellow-400" />
          <span className="text-xs font-bold text-yellow-400">VIP</span>
        </div>
      );
    }
    return null;
  };

  // Función para obtener el nivel de usuario
  const getUserLevel = (userData) => {
    if (isUserVip(userData)) {
      return { level: "VIP", color: "text-yellow-400", bg: "bg-yellow-500/20" };
    }
    return { level: "Free", color: "text-gray-400", bg: "bg-gray-500/20" };
  };

  // Función para insertar emoji
  const insertEmoji = (emoji) => {
    if (emojiPickerFor === "post") {
      setNewPost({ ...newPost, text: newPost.text + emoji });
    } else if (emojiPickerFor) {
      setNewComment({
        ...newComment,
        [emojiPickerFor]: (newComment[emojiPickerFor] || "") + emoji,
      });
    }
    setShowEmojiPicker(false);
    setEmojiPickerFor(null);
  };

  // Cargar publicaciones desde Firebase con filtros
  useEffect(() => {
    const postsRef = ref(database, "world/posts");
    let queryRef = postsRef;
    
            // Aplicar filtros
        if (filterType === "trending") {
          queryRef = query(postsRef, orderByChild("likes"), limitToLast(postLimit));
        } else if (filterType === "recent") {
          queryRef = query(postsRef, orderByChild("timestamp"), limitToLast(postLimit));
        } else if (filterType === "vip") {
          queryRef = query(postsRef, orderByChild("timestamp"), limitToLast(postLimit));
        } else if (filterType === "hashtags") {
          queryRef = query(postsRef, orderByChild("hashtagCount"), limitToLast(postLimit));
        }
    
    const unsubscribe = onValue(queryRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        let postsArray = Object.keys(data).map((key) => ({
          id: key,
          ...data[key],
        }));
        
        // Aplicar ordenamiento según el filtro
        if (filterType === "trending") {
          postsArray = postsArray.sort((a, b) => {
            const likesA = a.likes ? Object.keys(a.likes).length : 0;
            const likesB = b.likes ? Object.keys(b.likes).length : 0;
            return likesB - likesA;
          });
        } else if (filterType === "recent") {
          postsArray = postsArray.sort((a, b) => b.timestamp - a.timestamp);
        } else if (filterType === "vip") {
          postsArray = postsArray.filter(post => post.isVipUser || post.userVipStatus);
          postsArray = postsArray.sort((a, b) => b.timestamp - a.timestamp);
        } else if (filterType === "hashtags") {
          postsArray = postsArray.filter(post => post.hashtags && post.hashtags.length > 0);
          postsArray = postsArray.sort((a, b) => (b.hashtagCount || 0) - (a.hashtagCount || 0));
        } else {
          postsArray = postsArray.sort((a, b) => b.timestamp - a.timestamp);
        }
        
        setPosts(postsArray);
        setHasMorePosts(postsArray.length >= postLimit);
      } else {
        setPosts([]);
        setHasMorePosts(false);
      }
    });

    return () => unsubscribe();
  }, [filterType, postLimit]);

  // Cargar posts trending
  useEffect(() => {
    const postsRef = ref(database, "world/posts");
    const trendingQuery = query(postsRef, orderByChild("likes"), limitToLast(5));
    
    const unsubscribe = onValue(trendingQuery, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const trendingArray = Object.keys(data).map((key) => ({
          id: key,
          ...data[key],
        }));
        const sortedTrending = trendingArray
          .sort((a, b) => {
            const likesA = a.likes ? Object.keys(a.likes).length : 0;
            const likesB = b.likes ? Object.keys(b.likes).length : 0;
            return likesB - likesA;
          })
          .slice(0, 5);
        setTrendingPosts(sortedTrending);
      }
    });

    return () => unsubscribe();
  }, []);

  // Cargar hashtags trending
  useEffect(() => {
    loadTrendingHashtags();
  }, []);

  // Cargar comentarios
  useEffect(() => {
    const commentsRef = ref(database, "world/comments");
    const unsubscribe = onValue(commentsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setComments(data);
      }
    });

    return () => unsubscribe();
  }, []);

  // Cargar posts guardados del usuario
  useEffect(() => {
    const user = auth.currentUser;
    if (user) {
      const savedRef = ref(database, `users/${user.uid}/savedPosts`);
      const unsubscribe = onValue(savedRef, (snapshot) => {
        const data = snapshot.val();
        setSavedPosts(data || {});
      });

      return () => unsubscribe();
    }
  }, []);

  // Cargar estadísticas del usuario
  useEffect(() => {
    const user = auth.currentUser;
    if (user) {
      const statsRef = ref(database, `users/${user.uid}/stats`);
      const unsubscribe = onValue(statsRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          setUserStats(prev => ({ ...prev, [user.uid]: data }));
        }
      });

      return () => unsubscribe();
    }
  }, []);

  // Función para cargar más posts
  const loadMorePosts = async () => {
    if (isLoadingMore || !hasMorePosts) return;
    
    setIsLoadingMore(true);
    setPostLimit(prev => prev + 10);
    setIsLoadingMore(false);
  };

  // Función para filtrar posts
  const handleFilterChange = (filter) => {
    setFilterType(filter);
    setPostLimit(20);
    setHasMorePosts(true);
  };

  // Función para buscar posts
  const handleSearch = (query) => {
    setSearchQuery(query);
    // Implementar búsqueda en tiempo real
  };

  // Función para obtener ubicación del usuario
  const getUserLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          // Obtener nombre de la ciudad usando reverse geocoding
          fetch(`https://api.opencagedata.com/geocode/v1/json?q=${latitude}+${longitude}&key=YOUR_API_KEY`)
            .then(response => response.json())
            .then(data => {
              if (data.results && data.results[0]) {
                const city = data.results[0].components.city || 
                           data.results[0].components.town || 
                           data.results[0].components.village ||
                           'Ubicación desconocida';
                setUserLocation({ latitude, longitude, city });
              }
            })
            .catch(() => {
              setUserLocation({ latitude, longitude, city: 'Ubicación desconocida' });
            });
        },
        (error) => {
          console.error('Error obteniendo ubicación:', error);
          setError('No se pudo obtener tu ubicación');
        }
      );
    } else {
      setError('Tu navegador no soporta geolocalización');
    }
  };

  // Función para extraer hashtags del texto
  const extractHashtags = (text) => {
    const hashtagRegex = /#[\w\u0590-\u05ff]+/g;
    return text.match(hashtagRegex) || [];
  };

  // Función para actualizar estadísticas de hashtags
  const updateHashtagStats = async (hashtags) => {
    try {
      const hashtagRef = ref(database, 'world/hashtags');
      for (const hashtag of hashtags) {
        const hashtagKey = hashtag.replace('#', '');
        const hashtagStatsRef = ref(database, `world/hashtags/${hashtagKey}`);
        const snapshot = await get(hashtagStatsRef);
        
        if (snapshot.exists()) {
          const currentStats = snapshot.val();
          await update(hashtagStatsRef, {
            count: (currentStats.count || 0) + 1,
            lastUsed: Date.now(),
            posts: { ...currentStats.posts, [Date.now()]: true }
          });
        } else {
          await set(hashtagStatsRef, {
            hashtag: hashtag,
            count: 1,
            firstUsed: Date.now(),
            lastUsed: Date.now(),
            posts: { [Date.now()]: true }
          });
        }
      }
    } catch (error) {
      console.error('Error actualizando estadísticas de hashtags:', error);
    }
  };

  // Función para cargar hashtags trending
  const loadTrendingHashtags = async () => {
    try {
      const hashtagRef = ref(database, 'world/hashtags');
      const hashtagQuery = query(hashtagRef, orderByChild('count'), limitToLast(10));
      
      const unsubscribe = onValue(hashtagQuery, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const hashtagArray = Object.keys(data).map((key) => ({
            id: key,
            ...data[key],
          }));
          const sortedHashtags = hashtagArray
            .sort((a, b) => b.count - a.count)
            .slice(0, 10);
          setTrendingHashtags(sortedHashtags);
        }
      });

      return () => unsubscribe();
    } catch (error) {
      console.error('Error cargando hashtags trending:', error);
    }
  };

  // Cerrar emoji picker al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showEmojiPicker && !event.target.closest(".emoji-picker")) {
        setShowEmojiPicker(false);
        setEmojiPickerFor(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showEmojiPicker]);

  // Publicar una nueva publicación
  const handlePostSubmit = async (e) => {
    e.preventDefault();
    const user = auth.currentUser;
    if (!user || !newPost.text.trim()) return;

    // Validar contenido
    const validationError = validateContent(newPost.text);
    if (validationError) {
      setError(validationError);
      setTimeout(() => setError(""), 3000);
      return;
    }

    setIsPosting(true);
    setError("");

    try {
      let imageUrl = null;
      if (newPost.image) {
        const fileRef = storageRef(storage, `posts/${uuidv4()}`);
        await uploadBytes(fileRef, newPost.image);
        imageUrl = await getDownloadURL(fileRef);
      }

      // Extraer hashtags del texto
      const postHashtags = extractHashtags(newPost.text);
      
      const post = {
        text: newPost.text.trim(),
        imageUrl,
        userId: user.uid,
        userName: `${
          userData?.name || user.email?.split("@")[0] || "Anónimo"
        }${userData?.is_admin ? " ✅" : ""}`,
        userEmail: user.email,
        timestamp: Date.now(),
        likes: {},
        commentsCount: 0,
        // Información VIP del usuario
        isVipUser: isUserVip(userData),
        userVipStatus: userData?.typeU || "Cuenta Gratis",
        userLevel: getUserLevel(userData).level,
        // Estadísticas del usuario
        userTotalPosts: (userStats[user.uid]?.totalPosts || 0) + 1,
        userTotalLikes: userStats[user.uid]?.totalLikes || 0,
        // Hashtags y ubicación
        hashtags: postHashtags,
        location: userLocation,
        // Metadatos para trending
        hashtagCount: postHashtags.length,
        hasLocation: !!userLocation,
      };

      const postId = `${user.uid}_${Date.now()}`;
      const postsRef = ref(database, `world/posts/${postId}`);
      await set(postsRef, post);

      // Actualizar estadísticas del usuario
      const userStatsRef = ref(database, `users/${user.uid}/stats`);
      const currentStats = userStats[user.uid] || { totalPosts: 0, totalLikes: 0 };
      await update(userStatsRef, { 
        totalPosts: currentStats.totalPosts + 1,
        lastPostTime: Date.now()
      });

      // Actualizar estadísticas de hashtags
      if (postHashtags.length > 0) {
        await updateHashtagStats(postHashtags);
      }

      setNewPost({ text: "", image: null });
      setSelectedImage(null);
      setUserLocation(null);
    } catch (error) {
      console.error("Error al publicar:", error);
      setError("Error al publicar. Inténtalo de nuevo.");
      setTimeout(() => setError(""), 3000);
    } finally {
      setIsPosting(false);
    }
  };

  // Dar like a una publicación
  const handleLike = async (postId) => {
    const user = auth.currentUser;
    if (!user) return;

    try {
      const postRef = ref(database, `world/posts/${postId}`);
      const post = posts.find((p) => p.id === postId);

      if (post.likes && post.likes[user.uid]) {
        // Quitar like
        const updatedLikes = { ...post.likes };
        delete updatedLikes[user.uid];
        await update(postRef, { likes: updatedLikes });
        
        // Actualizar estadísticas del usuario que publicó
        if (post.userId !== user.uid) {
          const userStatsRef = ref(database, `users/${post.userId}/stats`);
          const currentStats = userStats[post.userId] || { totalLikes: 0 };
          await update(userStatsRef, { totalLikes: Math.max(0, currentStats.totalLikes - 1) });
        }
      } else {
        // Dar like
        const updatedLikes = { ...post.likes, [user.uid]: true };
        await update(postRef, { likes: updatedLikes });
        
        // Actualizar estadísticas del usuario que publicó
        if (post.userId !== user.uid) {
          const userStatsRef = ref(database, `users/${post.userId}/stats`);
          const currentStats = userStats[post.userId] || { totalLikes: 0 };
          await update(userStatsRef, { totalLikes: currentStats.totalLikes + 1 });
        }
      }
    } catch (error) {
      console.error("Error al dar like:", error);
    }
  };

  // Comentar en una publicación
  const handleComment = async (postId) => {
    const user = auth.currentUser;
    const commentText = newComment[postId];

    if (!user || !commentText?.trim()) return;

    // Validar contenido del comentario
    const validationError = validateContent(commentText);
    if (validationError) {
      setError(validationError);
      setTimeout(() => setError(""), 3000);
      return;
    }

    try {
      const commentId = uuidv4();
      const comment = {
        id: commentId,
        text: commentText.trim(),
        userId: user.uid,
        userName: user.name || user.email?.split("@")[0] || "Anónimo",
        userEmail: user.email,
        timestamp: Date.now(),
        postId: postId,
      };

      const commentRef = ref(database, `world/comments/${postId}/${commentId}`);
      await set(commentRef, comment);

      // Actualizar contador de comentarios
      const post = posts.find((p) => p.id === postId);
      const currentCount = post.commentsCount || 0;
      const postRef = ref(database, `world/posts/${postId}`);
      await update(postRef, { commentsCount: currentCount + 1 });

      setNewComment({ ...newComment, [postId]: "" });
    } catch (error) {
      console.error("Error al comentar:", error);
      setError("Error al comentar. Inténtalo de nuevo.");
      setTimeout(() => setError(""), 3000);
    }
  };

  // Guardar/desguardar publicación
  const handleSave = async (postId) => {
    const user = auth.currentUser;
    if (!user) return;

    try {
      const savedRef = ref(database, `users/${user.uid}/savedPosts/${postId}`);

      if (savedPosts[postId]) {
        await remove(savedRef);
      } else {
        await set(savedRef, { timestamp: Date.now() });
      }
    } catch (error) {
      console.error("Error al guardar:", error);
    }
  };

  // Compartir publicación (funcionalidad mejorada)
  const handleShare = async (post) => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Publicación de ${post.userName}`,
          text: `${post.text}\n\n${post.hashtags?.join(' ') || ''}`,
          url: `${window.location.origin}/post/${post.id}`,
        });
      } catch (error) {
        console.log("Error al compartir:", error);
      }
    } else {
      // Fallback: copiar al portapapeles
      const shareText = `${post.text}\n\n${post.hashtags?.join(' ') || ''}\n\n- ${post.userName}`;
      navigator.clipboard.writeText(shareText);
      setError("¡Copiado al portapapeles!");
      setTimeout(() => setError(""), 2000);
    }
  };

  // Función para copiar enlace del post
  const copyPostLink = (postId) => {
    const postUrl = `${window.location.origin}/post/${postId}`;
    navigator.clipboard.writeText(postUrl);
    setError("¡Enlace copiado!");
    setTimeout(() => setError(""), 2000);
  };

  // Función para reportar post
  const reportPost = (postId) => {
    // Implementar sistema de reportes
    setError("Post reportado. Gracias por tu feedback.");
    setTimeout(() => setError(""), 3000);
  };

  const formatTimeAgo = (timestamp) => {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return "Ahora";
    if (minutes < 60) return `${minutes}m`;
    if (hours < 24) return `${hours}h`;
    if (days < 7) return `${days}d`;
    return new Date(timestamp).toLocaleDateString();
  };

  if (loading) return <FootballLoading />;
  if (userError) return <p>Error: {userError}</p>;

  if (userData?.typeU !== "Cuenta Vip" && userData?.typeU !== "Cuenta Gratis" && userData?.type == '') {
    return <FreePay />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white">
      {/* Banner de error */}
      {error && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 animate-pulse">
          <div className="bg-red-500 text-white px-6 py-3 rounded-2xl flex items-center gap-3 shadow-2xl border border-red-400">
            <AlertCircle className="w-5 h-5" />
            <span className="font-medium">{error}</span>
          </div>
        </div>
      )}
      

      {/* Picker de Emojis */}
      {showEmojiPicker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end justify-center">
          <div className="emoji-picker bg-gray-800 rounded-t-3xl w-full max-w-md max-h-96 overflow-hidden border-t-4 border-purple-500 shadow-2xl">
            {/* Header del picker */}
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <h3 className="text-lg font-bold text-white">Emojis</h3>
              <button
                onClick={() => {
                  setShowEmojiPicker(false);
                  setEmojiPickerFor(null);
                }}
                className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center hover:bg-gray-600 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Categorías */}
            <div className="flex overflow-x-auto p-2 border-b border-gray-700 bg-gray-750">
              {Object.keys(emojiCategories).map((category) => (
                <button
                  key={category}
                  onClick={() => setActiveEmojiCategory(category)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap mr-2 transition-all ${
                    activeEmojiCategory === category
                      ? "bg-purple-600 text-white"
                      : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                  }`}
                >
                  {category === "frequent"
                    ? "Frecuentes"
                    : category === "smileys"
                    ? "Caritas"
                    : category === "emotions"
                    ? "Emociones"
                    : category === "gestures"
                    ? "Gestos"
                    : category === "objects"
                    ? "Objetos"
                    : "Símbolos"}
                </button>
              ))}
            </div>

            {/* Grid de emojis */}
            <div className="p-3 overflow-y-auto max-h-64">
              <div className="grid grid-cols-8 gap-2">
                {emojiCategories[activeEmojiCategory].map((emoji, index) => (
                  <button
                    key={index}
                    onClick={() => insertEmoji(emoji)}
                    className="w-10 h-10 rounded-lg hover:bg-gray-700 transition-colors flex items-center justify-center text-xl active:scale-95"
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de imagen */}
      {showImagePreview && (
        <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4">
          <div className="relative max-w-full max-h-full">
            <button
              onClick={() => setShowImagePreview(null)}
              className="absolute -top-12 right-0 text-white bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-70 transition-all"
            >
              <X className="w-6 h-6" />
            </button>
            <img
              src={showImagePreview}
              alt="Vista completa"
              className="max-w-full max-h-full object-contain rounded-lg"
            />
          </div>
        </div>
      )}

      <main className="max-w-4xl mx-auto px-3 pt-2 pb-20">
        {/* Header con filtros y búsqueda */}
        <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-3xl p-3 sm:p-4 mb-4 sm:mb-6 mt-2 sm:mt-3 shadow-2xl border border-purple-500/20 backdrop-blur-sm">
          <div className="flex flex-col gap-3 sm:gap-4">
            {/* Búsqueda */}
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
              <input
                type="text"
                placeholder="Buscar publicaciones..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 sm:py-3 bg-gray-700/50 rounded-xl sm:rounded-2xl border border-gray-600 focus:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-400/50 text-white placeholder-gray-400 backdrop-blur-sm text-sm sm:text-base"
              />
            </div>

            {/* Filtros */}
            <div className="flex flex-wrap gap-2 justify-center sm:justify-start">
              <button
                onClick={() => handleFilterChange("all")}
                className={`px-3 sm:px-4 py-2 rounded-lg sm:rounded-xl font-medium transition-all text-xs sm:text-sm ${
                  filterType === "all"
                    ? "bg-purple-600 text-white shadow-lg"
                    : "bg-gray-700/50 text-gray-300 hover:bg-gray-600/50"
                }`}
              >
                <Hash className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Todos</span>
                <span className="sm:hidden">All</span>
              </button>
              <button
                onClick={() => handleFilterChange("trending")}
                className={`px-3 sm:px-4 py-2 rounded-lg sm:rounded-xl font-medium transition-all text-xs sm:text-sm ${
                  filterType === "trending"
                    ? "bg-purple-600 text-white shadow-lg"
                    : "bg-gray-700/50 text-gray-300 hover:bg-gray-600/50"
                }`}
              >
                <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Trending</span>
                <span className="sm:hidden">🔥</span>
              </button>
              <button
                onClick={() => handleFilterChange("vip")}
                className={`px-3 sm:px-4 py-2 rounded-lg sm:rounded-xl font-medium transition-all text-xs sm:text-sm ${
                  filterType === "vip"
                    ? "bg-purple-600 text-white shadow-lg"
                    : "bg-gray-700/50 text-gray-300 hover:bg-gray-600/50"
                }`}
              >
                <Crown className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1 sm:mr-2" />
                <span className="hidden sm:inline">VIP</span>
                <span className="sm:hidden">👑</span>
              </button>
              <button
                onClick={() => handleFilterChange("recent")}
                className={`px-3 sm:px-4 py-2 rounded-lg sm:rounded-xl font-medium transition-all text-xs sm:text-sm ${
                  filterType === "recent"
                    ? "bg-purple-600 text-white shadow-lg"
                    : "bg-gray-700/50 text-gray-300 hover:bg-gray-600/50"
                }`}
              >
                <Clock className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Recientes</span>
                <span className="sm:hidden">⏰</span>
              </button>
              <button
                onClick={() => handleFilterChange("hashtags")}
                className={`px-3 sm:px-4 py-2 rounded-lg sm:rounded-xl font-medium transition-all text-xs sm:text-sm ${
                  filterType === "hashtags"
                    ? "bg-purple-600 text-white shadow-lg"
                    : "bg-gray-700/50 text-gray-300 hover:bg-gray-600/50"
                }`}
              >
                <Hash className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Hashtags</span>
                <span className="sm:hidden">#</span>
              </button>
            </div>
            
            {/* Hashtags Trending */}
            {trendingHashtags.length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-600/30">
                <div className="flex items-center gap-2 mb-2">
                  <Hash className="w-4 h-4 text-purple-400" />
                  <span className="text-xs text-gray-400 font-medium">Trending:</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {trendingHashtags.slice(0, 5).map((hashtag) => (
                    <button
                      key={hashtag.id}
                      onClick={() => setSelectedHashtag(hashtag.hashtag)}
                      className={`px-2 py-1 text-xs rounded-full border transition-all cursor-pointer ${
                        selectedHashtag === hashtag.hashtag
                          ? 'bg-purple-600 text-white border-purple-500'
                          : 'bg-purple-500/20 text-purple-300 border-purple-500/30 hover:bg-purple-500/30'
                      }`}
                    >
                      {hashtag.hashtag} ({hashtag.count})
                    </button>
                  ))}
                </div>
                
                {/* Hashtag seleccionado */}
                {selectedHashtag && (
                  <div className="mt-3 flex items-center gap-2">
                    <span className="text-xs text-gray-400">Filtrando por:</span>
                    <div className="px-3 py-1 bg-purple-600 text-white text-xs rounded-full border border-purple-500 flex items-center gap-2">
                      {selectedHashtag}
                      <button
                        onClick={() => setSelectedHashtag(null)}
                        className="hover:text-red-200 transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Botón para mostrar/ocultar trending */}
        <div className="flex justify-center mb-4 sm:mb-6">
          <button
            onClick={() => setShowTrending(!showTrending)}
            className="px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-xl sm:rounded-2xl font-bold transition-all transform hover:scale-105 shadow-lg flex items-center gap-2 text-sm sm:text-base"
          >
            {showTrending ? (
              <>
                <Eye className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="hidden sm:inline">Ocultar Trending</span>
                <span className="sm:hidden">Ocultar</span>
              </>
            ) : (
              <>
                <Flame className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="hidden sm:inline">Mostrar Trending</span>
                <span className="sm:hidden">Trending</span>
              </>
            )}
          </button>
        </div>

        {/* Posts Trending */}
        {showTrending && trendingPosts.length > 0 && (
          <div className="bg-gradient-to-r from-purple-800/20 to-pink-800/20 rounded-2xl sm:rounded-3xl p-3 sm:p-5 mb-4 sm:mb-6 border border-purple-500/30 backdrop-blur-sm">
            <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
              <Flame className="w-5 h-5 sm:w-6 sm:h-6 text-orange-400" />
              <h2 className="text-lg sm:text-xl font-bold text-white">🔥 Trending Ahora</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {trendingPosts.slice(0, 3).map((post) => (
                <div
                  key={post.id}
                  className="bg-gray-800/50 rounded-xl sm:rounded-2xl p-3 sm:p-4 border border-purple-500/20 hover:border-purple-400/40 transition-all cursor-pointer"
                  onClick={() => {
                    // Scroll al post en el feed
                    const postElement = document.getElementById(`post-${post.id}`);
                    if (postElement) {
                      postElement.scrollIntoView({ behavior: "smooth" });
                    }
                  }}
                >
                  <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                    <img
                      src={`https://avatar.iran.liara.run/public/boy?username=${post.userEmail}`}
                      alt={post.userName}
                      className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover border-2 border-purple-400"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1 sm:gap-2">
                        <span className="font-bold text-xs sm:text-sm text-white truncate">
                          {post.userName}
                        </span>
                        {post.isVipUser && (
                          <Crown className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-400 flex-shrink-0" />
                        )}
                      </div>
                      <p className="text-xs text-gray-400">
                        {post.likes ? Object.keys(post.likes).length : 0} likes
                      </p>
                    </div>
                  </div>
                  <p className="text-xs sm:text-sm text-gray-300 line-clamp-2">
                    {post.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Área para crear publicaciones */}
        <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-2xl sm:rounded-3xl p-3 sm:p-5 mb-4 sm:mb-6 shadow-2xl border border-purple-500/20 backdrop-blur-sm">
          <form onSubmit={handlePostSubmit} className="space-y-3 sm:space-y-4">
            <div className="flex items-start space-x-3 sm:space-x-4">
              <div className="relative flex-shrink-0">
                <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full p-0.5 bg-gradient-to-r ${getVipColor(userData)}`}>
                  <img
                    src={`https://avatar.iran.liara.run/public/boy?username=${auth.currentUser?.email}`}
                    alt="Tu avatar"
                    className="w-full h-full rounded-full object-cover border-2 border-gray-800 shadow-lg"
                  />
                </div>
                <div className="absolute -bottom-1 -right-1 w-3 h-3 sm:w-4 sm:h-4 bg-green-500 rounded-full border-2 border-gray-800"></div>
                {isUserVip(userData) && (
                  <div className="absolute -top-1 -left-1">
                    <Crown className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400 drop-shadow-lg" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                {/* Información del usuario */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-white text-sm sm:text-base">
                      {userData?.name || auth.currentUser?.email?.split("@")[0] || "Anónimo"}
                    </span>
                    {getVipBadge(userData)}
                    {userData?.is_admin && (
                      <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded-full border border-blue-500/30">
                        ✅ Admin
                      </span>
                    )}
                  </div>
                  <div className={`px-2 py-1 rounded-full text-xs font-medium ${getUserLevel(userData).bg} ${getUserLevel(userData).color} border border-current/30 w-fit`}>
                    {getUserLevel(userData).level}
                  </div>
                </div>
                <textarea
                  placeholder="¿Qué está pasando? 🤔"
                  value={newPost.text}
                  onChange={(e) =>
                    setNewPost({ ...newPost, text: e.target.value })
                  }
                  className="w-full p-3 sm:p-4 bg-gray-700/50 rounded-xl sm:rounded-2xl border border-gray-600 focus:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-400/50 placeholder-gray-400 resize-none backdrop-blur-sm transition-all text-base sm:text-lg"
                  rows="3"
                  maxLength="500"
                />
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs sm:text-sm text-gray-400 font-medium">
                    {newPost.text.length}/500
                  </span>
                  <div className="flex items-center gap-1">
                    <div className="w-20 sm:w-32 bg-gray-600 rounded-full h-1.5">
                      <div
                        className="bg-gradient-to-r from-purple-500 to-pink-500 h-1.5 rounded-full transition-all"
                        style={{
                          width: `${(newPost.text.length / 500) * 100}%`,
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {selectedImage && (
              <div className="relative mt-4 rounded-2xl overflow-hidden border border-gray-600 shadow-lg">
                <img
                  src={selectedImage}
                  alt="Vista previa"
                  className="max-h-72 w-full object-cover"
                />
                <button
                  type="button"
                  onClick={() => {
                    setSelectedImage(null);
                    setNewPost({ ...newPost, image: null });
                  }}
                  className="absolute top-3 right-3 bg-black bg-opacity-70 rounded-full p-2 hover:bg-opacity-90 transition-all backdrop-blur-sm"
                >
                  <X className="h-5 w-5 text-white" />
                </button>
              </div>
            )}

                <div className="flex flex-col sm:flex-row sm:items-center justify-between border-t border-gray-600/50 pt-3 sm:pt-4 gap-3 sm:gap-0">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                    {/* Estado VIP del usuario */}
                    {isUserVip(userData) && (
                      <div className="flex items-center gap-2 px-2 sm:px-3 py-1.5 sm:py-2 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 rounded-full border border-yellow-500/30">
                        <Crown className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-400" />
                        <span className="text-xs font-bold text-yellow-400">Usuario VIP</span>
                        <Star className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-400" />
                      </div>
                    )}
                    
                    <div className="flex items-center gap-1 sm:gap-2">
                      <label className="cursor-pointer flex items-center gap-1 sm:gap-2 text-purple-400 hover:text-purple-300 p-2 sm:p-3 rounded-full hover:bg-gray-700/50 transition-all group">
                        <ImageIcon className="h-5 w-5 sm:h-6 sm:w-6 group-hover:scale-110 transition-transform" />
                        <input
                          type="file"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              if (file.size > 5 * 1024 * 1024) {
                                setError("La imagen es muy grande (máx. 5MB)");
                                setTimeout(() => setError(""), 3000);
                                return;
                              }
                              setNewPost({ ...newPost, image: file });
                              setSelectedImage(URL.createObjectURL(file));
                            }
                          }}
                          accept="image/*"
                        />
                      </label>
                      <button
                        type="button"
                        onClick={() => {
                          setShowEmojiPicker(true);
                          setEmojiPickerFor("post");
                        }}
                        className="p-2 sm:p-3 text-purple-400 hover:text-purple-300 rounded-full hover:bg-gray-700/50 transition-all group"
                      >
                        <Smile className="h-5 w-5 sm:h-6 sm:w-6 group-hover:scale-110 transition-transform" />
                      </button>
                      <button
                        type="button"
                        onClick={getUserLocation}
                        className={`p-2 sm:p-3 rounded-full hover:bg-gray-700/50 transition-all group ${
                          userLocation ? 'text-green-400 bg-green-500/20' : 'text-purple-400 hover:text-purple-300'
                        }`}
                        title={userLocation ? `Ubicación: ${userLocation.city}` : 'Agregar ubicación'}
                      >
                        <MapPin className="h-5 w-5 sm:h-6 sm:w-6 group-hover:scale-110 transition-transform" />
                        {userLocation && (
                          <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-gray-800"></div>
                        )}
                      </button>
                    </div>
                    
                    {/* Indicador de ubicación */}
                    {userLocation && (
                      <div className="flex items-center gap-2 text-xs text-green-400 bg-green-500/10 px-3 py-1.5 rounded-full border border-green-500/20">
                        <MapPin className="w-3 h-3" />
                        <span>{userLocation.city}</span>
                        <button
                          onClick={() => setUserLocation(null)}
                          className="ml-1 hover:text-red-400 transition-colors"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </div>
                  <button
                    type="submit"
                    className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 sm:px-8 py-2.5 sm:py-3 rounded-full hover:from-purple-700 hover:to-pink-700 transition-all font-bold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 sm:gap-3 shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95 text-sm sm:text-base"
                    disabled={!newPost.text.trim() || isPosting}
                  >
                    {isPosting ? (
                      <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-b-2 border-white"></div>
                    ) : (
                      <Send className="h-4 w-4 sm:h-5 sm:w-5" />
                    )}
                    <span className="hidden sm:inline">
                      {isPosting ? "Publicando..." : "Publicar"}
                    </span>
                    <span className="sm:hidden">
                      {isPosting ? "..." : "Post"}
                    </span>
                  </button>
                </div>
          </form>
        </div>

        {/* Feed de publicaciones */}
        <div className="space-y-6">
          {posts.length === 0 ? (
            <div className="text-center py-16 bg-gradient-to-r from-gray-800/50 to-gray-900/50 rounded-3xl border border-gray-700/50 backdrop-blur-sm">
              <div className="mb-4">
                <MessageCircle className="w-16 h-16 mx-auto text-purple-400 mb-4" />
              </div>
              <p className="text-gray-300 text-xl font-medium">
                No hay publicaciones aún
              </p>
              <p className="text-gray-500 text-lg mt-2">
                ¡Sé el primero en compartir algo increíble! ✨
              </p>
            </div>
          ) : (
            <>
              {posts.map((post) => (
                                <article
                  key={post.id}
                  id={`post-${post.id}`}
                  className={`bg-gradient-to-r from-gray-800/80 to-gray-900/80 rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-2xl border transition-all backdrop-blur-sm hover:shadow-purple-500/10 ${
                    post.isVipUser 
                      ? 'border-yellow-500/30 hover:border-yellow-400/50' 
                      : 'border-gray-700/50 hover:border-purple-500/30'
                  }`}
                >
                  {/* Header del post */}
                  <div className="flex items-start justify-between mb-4 sm:mb-5">
                    <div className="flex items-center space-x-3 sm:space-x-4">
                      <div className="relative flex-shrink-0">
                        <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-full p-0.5 bg-gradient-to-r ${
                          post.isVipUser ? 'from-yellow-400 via-orange-500 to-red-500' : 'from-gray-400 to-gray-600'
                        }`}>
                          <img
                            src={`https://avatar.iran.liara.run/public/boy?username=${post.userEmail}`}
                            alt={post.userName}
                            className="w-full h-full rounded-full object-cover border-2 border-gray-800 shadow-lg"
                          />
                        </div>
                        <div className="absolute -bottom-1 -right-1 w-3 h-3 sm:w-4 sm:h-4 bg-green-500 rounded-full border-2 border-gray-800"></div>
                        {post.isVipUser && (
                          <div className="absolute -top-1 -left-1">
                            <Crown className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-400 drop-shadow-lg" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 mb-2">
                          <h3 className="font-bold text-lg sm:text-xl text-white truncate">
                            {post.userName}
                          </h3>
                          <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                            {post.isVipUser && (
                              <div className="flex items-center gap-1 px-2 py-1 bg-yellow-500/20 rounded-full border border-yellow-500/30">
                                <Crown className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-400" />
                                <span className="text-xs font-bold text-yellow-400">VIP</span>
                              </div>
                            )}
                            {post.userLevel === "VIP" && (
                              <div className="px-2 py-1 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 rounded-full border border-yellow-500/30">
                                <span className="text-xs font-bold text-yellow-400">⭐ Premium</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-400">
                          <span className="font-medium">
                            {formatTimeAgo(post.timestamp)}
                          </span>
                          {post.userTotalPosts > 0 && (
                            <span className="flex items-center gap-1">
                              <Users className="w-3 h-3 sm:w-4 sm:h-4" />
                              {post.userTotalPosts} posts
                            </span>
                          )}
                          {post.userTotalLikes > 0 && (
                            <span className="flex items-center gap-1">
                              <Heart className="w-3 h-3 sm:w-4 sm:h-4" />
                              {post.userTotalLikes} likes
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 sm:gap-2">
                      {post.isVipUser && (
                        <div className="px-2 sm:px-3 py-1 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 rounded-full border border-yellow-500/30">
                          <span className="text-xs font-bold text-yellow-400">🔥 VIP</span>
                        </div>
                      )}
                      <div className="relative">
                        <button 
                          className="text-gray-400 hover:text-white p-1.5 sm:p-2 rounded-full hover:bg-gray-700/50 transition-all"
                          onClick={() => setShowComments({ ...showComments, [`menu-${post.id}`]: !showComments[`menu-${post.id}`] })}
                        >
                          <MoreHorizontal className="h-5 w-5 sm:h-6 sm:w-6" />
                        </button>
                        
                        {/* Menú desplegable */}
                        {showComments[`menu-${post.id}`] && (
                          <div className="absolute right-0 top-full mt-1 w-48 bg-gray-800 rounded-xl border border-gray-600 shadow-2xl z-10">
                            <div className="p-2 space-y-1">
                              <button
                                onClick={() => copyPostLink(post.id)}
                                className="w-full text-left px-3 py-2 text-sm text-gray-300 hover:bg-gray-700 rounded-lg transition-colors flex items-center gap-2"
                              >
                                <Share className="w-4 h-4" />
                                Copiar enlace
                              </button>
                              <button
                                onClick={() => handleShare(post)}
                                className="w-full text-left px-3 py-2 text-sm text-gray-300 hover:bg-gray-700 rounded-lg transition-colors flex items-center gap-2"
                              >
                                <Share className="w-4 h-4" />
                                Compartir
                              </button>
                              <button
                                onClick={() => reportPost(post.id)}
                                className="w-full text-left px-3 py-2 text-sm text-red-400 hover:bg-red-500/20 rounded-lg transition-colors flex items-center gap-2"
                              >
                                <AlertCircle className="w-4 h-4" />
                                Reportar
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                {/* Contenido del post */}
                <div className="mb-4 sm:mb-5">
                  <p className="text-gray-100 leading-relaxed whitespace-pre-wrap text-base sm:text-lg">
                    {post.text}
                  </p>
                  
                  {/* Hashtags */}
                  {post.hashtags && post.hashtags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {post.hashtags.map((hashtag, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-purple-500/20 text-purple-300 text-xs rounded-full border border-purple-500/30 cursor-pointer hover:bg-purple-500/30 transition-all"
                          onClick={() => setSelectedHashtag(hashtag)}
                        >
                          {hashtag}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  {/* Ubicación */}
                  {post.location && (
                    <div className="flex items-center gap-2 mt-3 text-sm text-gray-400">
                      <MapPin className="w-4 h-4 text-green-400" />
                      <span>{post.location.city}</span>
                    </div>
                  )}
                </div>

                {/* Imagen del post */}
                {post.imageUrl && (
                  <div className="mb-4 sm:mb-5 rounded-xl sm:rounded-2xl overflow-hidden border border-gray-600 shadow-lg">
                    <img
                      src={post.imageUrl}
                      alt="Publicación"
                      className="w-full object-cover max-h-72 sm:max-h-96 cursor-pointer hover:opacity-90 transition-all transform hover:scale-105"
                      onClick={() => setShowImagePreview(post.imageUrl)}
                    />
                  </div>
                )}

                {/* Botones de interacción */}
                <div className="flex items-center justify-between pt-4 sm:pt-5 border-t border-gray-600/50">
                  <div className="flex items-center gap-4 sm:gap-8">
                    <button
                      onClick={() => handleLike(post.id)}
                      className={`flex items-center gap-2 sm:gap-3 transition-all transform hover:scale-110 active:scale-95 ${
                        post.likes && post.likes[auth.currentUser?.uid]
                          ? "text-red-500 hover:text-red-400"
                          : "text-gray-400 hover:text-red-500"
                      }`}
                    >
                      <Heart
                        className={`h-5 w-5 sm:h-6 sm:w-6 ${
                          post.likes && post.likes[auth.currentUser?.uid]
                            ? "fill-current animate-pulse"
                            : ""
                        }`}
                      />
                      <span className="text-xs sm:text-sm font-bold">
                        {post.likes ? Object.keys(post.likes).length : 0}
                      </span>
                    </button>

                    <button
                      onClick={() =>
                        setShowComments({
                          ...showComments,
                          [post.id]: !showComments[post.id],
                        })
                      }
                      className="flex items-center gap-2 sm:gap-3 text-gray-400 hover:text-blue-400 transition-all transform hover:scale-110 active:scale-95"
                    >
                      <MessageCircle className="h-5 w-5 sm:h-6 sm:w-6" />
                      <span className="text-xs sm:text-sm font-bold">
                        {post.commentsCount || 0}
                      </span>
                    </button>

                    <button
                      onClick={() => handleShare(post)}
                      className="flex items-center gap-2 sm:gap-3 text-gray-400 hover:text-green-400 transition-all transform hover:scale-110 active:scale-95"
                    >
                      <Share className="h-5 w-5 sm:h-6 sm:w-6" />
                      <span className="text-xs sm:text-sm font-bold hidden sm:inline">
                        Compartir
                      </span>
                    </button>
                  </div>

                  <button
                    onClick={() => handleSave(post.id)}
                    className={`p-2 sm:p-3 rounded-full transition-all transform hover:scale-110 active:scale-95 ${
                      savedPosts[post.id]
                        ? "text-yellow-500 hover:text-yellow-400 bg-yellow-500/20"
                        : "text-gray-400 hover:text-yellow-500 hover:bg-gray-700/50"
                    }`}
                  >
                    <Bookmark
                      className={`h-5 w-5 sm:h-6 sm:w-6 ${
                        savedPosts[post.id] ? "fill-current" : ""
                      }`}
                    />
                  </button>
                </div>

                {/* Sección de comentarios */}
                {showComments[post.id] && (
                  <div className="mt-6 pt-6 border-t border-gray-600/50">
                    {/* Formulario para comentar */}
                    <div className="flex items-center gap-4 mb-6">
                      <img
                        src={`https://avatar.iran.liara.run/public/boy?username=${auth.currentUser?.email}`}
                        alt="Tu avatar"
                        className="w-10 h-10 rounded-full object-cover border-2 border-purple-400 shadow-md"
                      />
                      <div className="flex-1 flex gap-3">
                        <div className="relative flex-1">
                          <input
                            type="text"
                            placeholder="Escribe un comentario... 💭"
                            value={newComment[post.id] || ""}
                            onChange={(e) =>
                              setNewComment({
                                ...newComment,
                                [post.id]: e.target.value,
                              })
                            }
                            className="w-full px-4 py-3 bg-gray-700/50 rounded-full border border-gray-600 focus:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-400/50 text-sm backdrop-blur-sm pr-12"
                            maxLength="200"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setShowEmojiPicker(true);
                              setEmojiPickerFor(post.id);
                            }}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-purple-400 transition-colors"
                          >
                            <Smile className="h-5 w-5" />
                          </button>
                        </div>
                        <button
                          onClick={() => handleComment(post.id)}
                          disabled={!newComment[post.id]?.trim()}
                          className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 rounded-full hover:from-purple-700 hover:to-pink-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 active:scale-95 shadow-lg"
                        >
                          <Send className="h-5 w-5" />
                        </button>
                      </div>
                    </div>

                    {/* Lista de comentarios */}
                    <div className="space-y-4 max-h-72 overflow-y-auto custom-scrollbar">
                      {comments[post.id] &&
                        Object.values(comments[post.id])
                          .sort((a, b) => b.timestamp - a.timestamp)
                          .map((comment) => (
                            <div
                              key={comment.id}
                              className="flex items-start gap-4 p-3 rounded-2xl bg-gray-700/30 hover:bg-gray-700/50 transition-all backdrop-blur-sm"
                            >
                              <img
                                src={`https://avatar.iran.liara.run/public/boy?username=${comment.userEmail}`}
                                alt={comment.userName}
                                className="w-8 h-8 rounded-full object-cover border-2 border-gray-500 flex-shrink-0"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-3 mb-1">
                                  <span className="font-bold text-sm text-white">
                                    {comment.userName}
                                  </span>
                                  <span className="text-xs text-gray-400">
                                    {formatTimeAgo(comment.timestamp)}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-100 leading-relaxed break-words">
                                  {comment.text}
                                </p>
                              </div>
                            </div>
                          ))}
                    </div>
                  </div>
                )}
              </article>
            ))}
            
            {/* Botón de cargar más posts */}
            {hasMorePosts && (
              <div className="text-center py-6 sm:py-8">
                <button
                  onClick={loadMorePosts}
                  disabled={isLoadingMore}
                  className="px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-xl sm:rounded-2xl font-bold transition-all transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 sm:gap-3 mx-auto text-sm sm:text-base"
                >
                  {isLoadingMore ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-b-2 border-white"></div>
                      <span className="hidden sm:inline">Cargando...</span>
                      <span className="sm:hidden">...</span>
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
                      <span className="hidden sm:inline">Cargar Más Posts</span>
                      <span className="sm:hidden">Más Posts</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </main>

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(55, 65, 81, 0.3);
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(139, 92, 246, 0.5);
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(139, 92, 246, 0.7);
        }
        
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        
        .vip-glow {
          box-shadow: 0 0 20px rgba(255, 193, 7, 0.3);
        }
        
        .vip-glow:hover {
          box-shadow: 0 0 30px rgba(255, 193, 7, 0.5);
        }
        
        .trending-pulse {
          animation: trending-pulse 2s infinite;
        }
        
        @keyframes trending-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
      `}</style>
    </div>
  );
}
